import {forwardRef, Fragment, useImperativeHandle, useState} from 'react';
import {colors, sizes} from 'patient-ping-remedy/packages/theme';
import Icon, {ICONS} from 'patient-ping-remedy/packages/icon';
import {HintTypography, StandardSemiBoldTypography} from 'patient-ping-remedy/packages/typography';
import ToolTip from 'patient-ping-remedy/packages/tool-tip';
import ProgressIndicator, {lighterGreen, StepOptions} from 'patient-ping-remedy/packages/progress-indicator';
import {
  CareJourneyDetailsResponse,
  CareJourneyEventType,
  CareJourneyStep,
  ScheduleAppointmentJourneyResponse,
  TaskStepName
} from '@/api/dto/care-journey';
import {useTaskStepName} from '@/hooks/useTaskStepName';
import TaskEditDetails from '@/components/pages/tasks/task_edit/progress/TaskEditDetails';
import TaskEditStep from '@/components/pages/tasks/task_edit/progress/TaskEditStep';
import WorkflowStep from '@/components/pages/tasks/task_edit/progress/WorkflowStep';
import {
  AppointmentLabel,
  ContentSection,
  Progress,
  ProgressSection,
  ProgressSectionContent,
  ProgressSectionInner,
  ProgressText,
  ProgressWrapper,
  StepLabel,
  Wrapper
} from '@/components/pages/tasks/task_edit/progress/TaskEditProgress.styles';
import {FilterInvalidate} from "@common/FilterComponent";
import {css} from "@emotion/css";
import {
  appointmentTypes, getAppointmentStatus
} from "@/components/pages/tasks/task_edit/progress/bh_journey/ScheduleAppointmentsStep.constants";
import AppointmentDetails from "@/components/pages/tasks/task_edit/progress/bh_journey/AppointmentDetails";
import {useNavigate} from "react-router-dom";
import {SearchParam} from "@/helpers/search_params";
import {useActiveRoster} from "@/store/roster_store";

export const statusDisplayValues: Record<string, { label: string, icon?: ICONS }> = {
  pending: {
    label: 'Pending',
  },
  in_progress: {
    label: 'In progress',
    icon: ICONS.circle,
  },
  skipped: {
    label: 'Skipped',
    icon: ICONS.circle,
  },
  completed: {
    label: 'Completed',
    icon: ICONS.check,
  },
};

export type TaskEditProgressRef = {
  changeTab(index: number): void;
}

type Props = {
  careJourneyDetails: CareJourneyDetailsResponse;
  fetching: boolean;
  disabled: boolean;
  invalidate: FilterInvalidate;
}

const appointmentJourneyTypes = [
  CareJourneyEventType.BH_APPOINTMENT.valueOf(),
  CareJourneyEventType.BH_SCHEDULE_APPOINTMENT.valueOf()
];

const TaskEditProgress = forwardRef<TaskEditProgressRef, Props>((
  { careJourneyDetails, fetching, disabled, invalidate }, ref) => {
  const { taskStepDisplayValues } = useTaskStepName();
  const [current, setCurrent] = useState(-1);
  const currentRoster = useActiveRoster();
  const navigate = useNavigate();

  const value = careJourneyDetails.steps.filter((step) => step.status === 'completed').length;
  const max = careJourneyDetails.steps.length;

  useImperativeHandle(ref, () => {
    return {
      changeTab(tabIndex: number) {
        setCurrent(tabIndex);
      },
    };
  }, []);
  
  const getDescription = (step: CareJourneyStep) => {
    if(step.stepName !== TaskStepName.SCHEDULED_APPOINTMENTS) {
      return <Fragment>{statusDisplayValues[step.status]?.label}</Fragment>;
    }

    return <Fragment>
      <div className={css({marginBottom: sizes.small})}>{statusDisplayValues[step.status]?.label}</div>
      <ProgressIndicator
        direction="vertical"
        current={-1}
        steps={step.appointments?.map((appointment, index) => {
          const appointmentStatusConfig = getAppointmentStatus(appointment);
          const needsReview = appointmentStatusConfig.status === 'Needs review';
          const inProgress = appointmentStatusConfig.status === 'In progress';

          return {
            label: appointmentTypes[appointment.providerType as keyof typeof appointmentTypes] ?? appointment.providerType,
            value: index,
            description: appointmentStatusConfig.progressStatus,
            color: needsReview ? colors.white : lighterGreen,
            icon: () => needsReview ? <Icon
              className={css({width: sizes.small, height: sizes.small,})}
              iconClass={ICONS['exclamation-triangle']}
              iconSize="1x"
              color={colors.yellow5}
              weight="far"
              data-testid="ai-agent-fail"
            /> : <Icon
              iconClass={inProgress ? ICONS.circle : ICONS.check}
              color={lighterGreen}
            />
          };
        }) ?? []}
        handleClick={(_, stepOptions) => {
          const appointment = step.appointments?.at(stepOptions?.value ?? 0);
          if(appointment) {
            const searchParams = `${SearchParam.TASK_ID}=${appointment.parentJourneyId}&` +
                `${SearchParam.ROSTER_ID}=${currentRoster?.id}&` +
                `${SearchParam.APPOINTMENT_ID}=${appointment.careJourneyInstanceId}`;
            navigate(`/tasks/details/appointment?${searchParams}`);
          }
        }}
      />
    </Fragment>;
  };

  const navigateToParentJourney = () => {
    const appointment = careJourneyDetails as ScheduleAppointmentJourneyResponse;
    const search = `?${SearchParam.TASK_ID}=${appointment.parentJourneyId}`
    + `&${SearchParam.ROSTER_ID}=${currentRoster?.id}`;
    navigate(`/tasks/details${search}`);
  };

  return (
    <Wrapper>
      <ProgressSection>
        {appointmentJourneyTypes.includes(careJourneyDetails.journeyType) &&
          <div className={css({display: 'flex', padding: sizes.small, paddingBottom: 0})}>
            <div
              className={css({marginRight:  sizes.xxSmall, cursor: 'pointer'})}
              onClick={navigateToParentJourney}
            >
              <Icon
                iconClass={ICONS["angle-left"]}
                iconSize={"2x"}
                weight={"fal"}
                color={colors.primary1}
              />
            </div>
            <div className={css({display: 'flex', flexDirection: 'column', justifyContent: "center"})}>
              <AppointmentLabel>
                {appointmentTypes[(careJourneyDetails as ScheduleAppointmentJourneyResponse).providerType]
                  ?? (careJourneyDetails as ScheduleAppointmentJourneyResponse).providerType}
              </AppointmentLabel>
              <HintTypography>
                BH Care Management
              </HintTypography>
            </div>
          </div>
        }
        <ProgressSectionContent>
          <ProgressSectionInner>
            <ProgressWrapper>
              <ProgressText>
            Complete {value} out of {max} steps
                <StandardSemiBoldTypography>{Math.round((value * 100) / max)}%</StandardSemiBoldTypography>
              </ProgressText>
              <Progress value={value} max={max} />
            </ProgressWrapper>
            <ProgressIndicator
              steps={[
                {
                  label: (
                  <StepLabel>
                    {'Details'}
                    {careJourneyDetails.orchestrationEngineDetails
                      && careJourneyDetails.orchestrationEngineDetails.actor === 'human'
                      && careJourneyDetails.orchestrationEngineDetails.reasonStep !== null && (
                      <ToolTip
                        text={"AI Agent couldn't complete " +
                          (taskStepDisplayValues[careJourneyDetails.orchestrationEngineDetails.reasonStep as TaskStepName]
                            ?? careJourneyDetails.orchestrationEngineDetails.reasonStep) +
                          " step"}
                        type="dark"
                      >
                        <Icon
                          iconClass={ICONS['exclamation-triangle']}
                          iconSize="1x"
                          color={colors.yellow5}
                          weight="far"
                          data-testid="ai-agent-fail"
                        />
                      </ToolTip>
                    )}
                  </StepLabel>
              ) as unknown as string,
                  value: -1,
                  color: colors.blue4,
                  icon: (step) =>
                    <Icon iconClass={ICONS.info} color={step.value === current ? colors.white : step.color} />,
                },
                ...careJourneyDetails.steps.map((step, index) => ({
                  label: (
                <StepLabel>
                  {!careJourneyDetails.orchestrationEngineDetails && step.displayLabel
                    ? step.displayLabel
                    : taskStepDisplayValues[step.stepName as TaskStepName] ?? step.stepName}
                </StepLabel>
              ) as unknown as string,
                  value: index,
                  description: getDescription(step) as unknown as string,
                  color: current === index ? colors.green4 : lighterGreen,
                  icon: (stepValue: StepOptions) => statusDisplayValues[step.status]?.icon
                    ? <Icon
                      iconClass={statusDisplayValues[step.status].icon!}
                      iconSize={statusDisplayValues[step.status].icon === ICONS.circle ? 'xs' : 'sm'}
                      color={stepValue.value === current ? colors.white : stepValue.color}
                    />
                    : <div />,
                }))
              ]}
              current={current}
              direction="vertical"
              highlightCurrent
              handleClick={(_, step) => {
                setCurrent(step?.value ?? 0);
              }}
            />
          </ProgressSectionInner>
        </ProgressSectionContent>
      </ProgressSection>
      <ContentSection>
        {current === -1
          ? careJourneyDetails.journeyType === CareJourneyEventType.BH_SCHEDULE_APPOINTMENT ?
            <AppointmentDetails
              careJourneyDetails={careJourneyDetails as ScheduleAppointmentJourneyResponse}
              changeStep={setCurrent}
              invalidate={invalidate}
              disabled={disabled}
            />
            :  <TaskEditDetails 
              careJourneyDetails={careJourneyDetails} 
              changeStep={setCurrent}
              invalidate={invalidate}
              disabled={disabled} />
          : 
          careJourneyDetails.orchestrationEngineDetails ?
            <TaskEditStep
              patient={careJourneyDetails.clientProfile}
              readOnly={disabled || careJourneyDetails.userAssignment.assignedToAi}
              careJourneyStep={careJourneyDetails.steps[current]}
              careJourneyDetails={careJourneyDetails}
              fetching={fetching}
              invalidate={invalidate}
            /> :
            <WorkflowStep
              careJourneyStep={careJourneyDetails.steps[current]}
              careJourneyDetails={careJourneyDetails}
              fetching={fetching}
              disabled={disabled}
            />}
      </ContentSection>
    </Wrapper>
  );
});

export default TaskEditProgress;
